import { Badge } from "@/components/ui/badge";
import { Award, Hammer, Heart, MapPin, Quote } from "lucide-react";

const MILESTONES = [
    { year: "2006", title: "Création de Karedess", desc: "Sébastien fonde son agence à Mulhouse à seulement quelques années de ses premiers sites web." },
    { year: "2010+", title: "Outils internes", desc: "Premiers outils SaaS développés en interne pour automatiser les tâches répétitives de l'agence." },
    { year: "2020+", title: "Mûris sur le terrain", desc: "Des outils testés sur des centaines de clients, des restaurants aux centres de formation." },
    { year: "Aujourd'hui", title: "Ouverts au monde", desc: "Web Me Up libère ces outils pour toutes les agences qui veulent gagner du temps et du CA." },
];

export default function StorySection() {
    return (
        <section id="histoire" className="relative py-24 sm:py-32">
            <div className="pointer-events-none absolute inset-0 -z-10 opacity-50">
                <div className="absolute left-1/4 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />
            </div>

            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
                    {/* Left: Founder card */}
                    <div className="lg:col-span-5">
                        <div className="sticky top-28">
                            <FounderCard />
                        </div>
                    </div>

                    {/* Right: Story narrative */}
                    <div className="lg:col-span-7">
                        <Badge variant="outline" className="rounded-full border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground">
                            L'histoire · Le pourquoi
                        </Badge>
                        <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                            Des outils créés par une agence,<br />
                            <span className="text-gradient">pour des agences.</span>
                        </h2>
                        <p className="mt-5 text-base text-muted-foreground sm:text-lg">
                            Sébastien Schueller façonne son premier site à 14 ans. Près de <strong className="text-foreground">20 ans plus tard</strong>, il dirige Karedess, livre 30 à 40 nouveaux sites par an, et a accumulé une obsession : <em>simplifier la vie quotidienne des agences web</em>. Web Me Up est l'aboutissement de cette obsession — chaque outil de la suite a d'abord été utilisé en interne, raffiné sur le terrain, puis ouvert au monde.
                        </p>

                        <figure className="mt-8 rounded-2xl border border-border bg-gradient-card p-6">
                            <Quote className="h-6 w-6 text-primary" />
                            <blockquote className="mt-3 font-display text-lg font-medium leading-relaxed text-foreground sm:text-xl">
                                « J'ai passé 20 ans à construire ces outils pour moi-même.
                                Aujourd'hui, je les ouvre à toutes les agences qui veulent
                                travailler plus efficacement et facturer plus, sans complexité. »
                            </blockquote>
                            <figcaption className="mt-4 flex items-center gap-3">
                                <span className="h-px flex-1 bg-border" />
                                <span className="text-xs font-medium text-muted-foreground">
                                    Sébastien Schueller — Fondateur, Karedess & Web Me Up
                                </span>
                            </figcaption>
                        </figure>

                        {/* Timeline */}
                        <ol className="mt-10 space-y-5">
                            {MILESTONES.map((m, i) => (
                                <li key={m.year} className="group relative flex gap-5 rounded-xl border border-transparent p-3 transition-colors hover:border-border hover:bg-card/40">
                                    <div className="relative flex flex-col items-center">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-card text-xs font-mono font-semibold text-primary ring-1 ring-border">
                                            {i + 1}
                                        </div>
                                        {i < MILESTONES.length - 1 && (
                                            <span className="mt-2 h-full w-px flex-1 bg-gradient-to-b from-border to-transparent" />
                                        )}
                                    </div>
                                    <div className="flex-1 pb-2">
                                        <div className="flex items-baseline gap-3">
                                            <span className="font-mono text-xs uppercase tracking-wider text-primary">{m.year}</span>
                                            <h3 className="font-display text-base font-semibold text-foreground">{m.title}</h3>
                                        </div>
                                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </section>
    );
}

const FounderCard = () => (
    <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-card p-1">
        <div className="rounded-[calc(1.5rem-4px)] bg-background/60 p-6 sm:p-8">
            {/* Profile avatar block */}
            <div className="relative">
                <div className="relative mx-auto flex h-32 w-32 items-center justify-center rounded-2xl bg-gradient-primary shadow-glow">
                    <div className="absolute inset-1 rounded-xl bg-background/30 backdrop-blur" />
                    <span className="relative font-display text-5xl font-semibold text-primary-foreground">SS</span>
                </div>
                <div className="absolute -right-2 -top-2 inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-success ring-1 ring-success/30">
                    <span className="h-1.5 w-1.5 animate-pulse-glow rounded-full bg-success" /> Actif
                </div>
            </div>

            <div className="mt-6 text-center">
                <h3 className="font-display text-xl font-semibold text-foreground">Sébastien Schueller</h3>
                <p className="text-sm text-muted-foreground">Fondateur · Karedess & Web Me Up</p>
            </div>

            {/* Big stat */}
            <div className="mt-6 rounded-2xl border border-border bg-card/60 p-5 text-center">
                <div className="font-display text-5xl font-semibold text-gradient">~20</div>
                <div className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                    années d'expérience web
                </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-2 text-xs">
                <Tag icon={MapPin}>Mulhouse · Grand Est</Tag>
                <Tag icon={Award}>Master 2 admin. d'entreprises · Cert. RSE</Tag>
                <Tag icon={Hammer}>30-40 sites livrés / an chez Karedess</Tag>
                <Tag icon={Heart}>Obsédé par la simplicité &amp; le terrain</Tag>
            </div>

            <a
                href="https://karedess.agency/a-propos"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-border bg-card/40 px-4 py-2.5 text-xs font-medium text-foreground transition-colors hover:bg-card"
            >
                Lire son portrait complet ↗
            </a>
        </div>
    </div>
);

const Tag = ({ icon: Icon, children }) => (
    <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-background/40 px-3 py-2 text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" />
        <span className="text-foreground/80">{children}</span>
    </div>
);
